// Physical control panel (two USB button boxes seen by the browser as gamepads).
// Default map follows the operator's layout; any entry can be overridden from Setup -> Console.

const controller = (index, name, type = "button") => ({ device: "controller", kind: "button", index, name, type });
const macro = (index, name, type = "button") => ({ device: "macro", kind: "button", index, name, type });

export const DEFAULT_MAP = {
  "c.b0": controller(0, "Emergency Purge"),
  "c.b1": controller(1, "Containment Release"),
  "c.b2": controller(2, "Primary Ignition"),
  "c.b3": controller(3, "Reactor Pulse"),
  "c.b4": controller(4, "Override Bypass"),
  "c.b5": controller(5, "Grid Sync"),
  "c.b8": controller(8, "Manual Reset"),
  "c.b9": controller(9, "System Confirm"),
  "c.b6": controller(6, "Auxiliary Pump", "toggle"),
  "c.b7": controller(7, "Backup Filter", "toggle"),
  "c.b12": controller(12, "Coolant Flow", "toggle"),
  "c.b14": controller(14, "Pressure Seal", "toggle"),
  "c.b16": controller(16, "Containment Field", "toggle"),
  "c.b18": controller(18, "Main Grid", "toggle"),
  "c.axis9": { device: "controller", kind: "axis", index: 9, name: "Power Regulator", type: "dial" },
  "m.b15": macro(15, "Plasma Coil"),
  "m.b17": macro(17, "Capacitor Charge"),
  "m.b18": macro(18, "Surge Initiate"),
  "m.b19": macro(19, "Thermal Core"),
  "m.b5": macro(5, "Fuel Inject"),
  "m.b7": macro(7, "Dynamo Pulse"),
  "m.b8": macro(8, "Valve Release"),
  "m.b9": macro(9, "Emergency Vent"),
  "m.b0": macro(0, "Sector 1 Power"),
  "m.b2": macro(2, "Sector 2 Power"),
  "m.b3": macro(3, "Sector 3 Power"),
  "m.b4": macro(4, "Sector 4 Power"),
  "m.b20": macro(20, "Generator 1", "toggle"),
  "m.b21": macro(21, "Generator 2", "toggle"),
  "m.b22": macro(22, "Generator 3", "toggle"),
  "m.b23": macro(23, "Generator 4", "toggle"),
  "m.b10": macro(10, "Primary Breaker", "toggle"),
  "m.b11": macro(11, "Master Key", "toggle"),
  "m.b12": macro(12, "Secondary Breaker", "toggle"),
  "m.dial1": { device: "macro", kind: "encoder", cw: 25, ccw: 24, name: "Oxygen Level", type: "dial" },
  "m.dial2": { device: "macro", kind: "encoder", cw: 26, ccw: 27, name: "Fuel Ratio", type: "dial" },
  "m.dial3": { device: "macro", kind: "encoder", cw: 28, ccw: 29, name: "Core Output", type: "dial" },
  "m.dial4": { device: "macro", kind: "encoder", cw: 30, ccw: 31, name: "Thermal Limit", type: "dial" },
};

export const DEFAULT_DEVICES = { controller: "PXN", macro: "" };
export const CONFIRM_ID = "c.b9";
export const DEVICE_LABEL = { controller: "PXN Controller", macro: "Macro Box" };
// Controls the operator reported as not reaching the browser (mouse-click buttons, dead switch/encoders): off by default.
export const OFF_BY_DEFAULT = ["c.b9", "m.b11", "m.dial1", "m.dial2", "m.dial3", "m.dial4"];

export const mergeMap = (overrides = {}) => {
  const out = {};
  for (const [id, base] of Object.entries(DEFAULT_MAP)) out[id] = { enabled: !OFF_BY_DEFAULT.includes(id), ...base, ...(overrides[id] || {}) };
  return out;
};

export const controlsOf = (map, type) => Object.entries(map).filter(([, c]) => c.type === type).map(([id, c]) => ({ id, ...c }));

// Controls the lock may ask for: enabled in Setup and (when any box is connected) on a connected box.
export const playable = (map, type, roles = []) =>
  controlsOf(map, type).filter((c) => c.enabled !== false && (!roles.length || roles.includes(c.device)));

// Which role a gamepad plays, from its id string and the configured hints.
export const roleFor = (gamepadId, devices, others = []) => {
  const id = (gamepadId || "").toLowerCase();
  const hints = { ...DEFAULT_DEVICES, ...devices };
  if (hints.controller && id.includes(hints.controller.toLowerCase())) return "controller";
  if (hints.macro && id.includes(hints.macro.toLowerCase())) return "macro";
  if (others.includes("controller") && !others.includes("macro")) return "macro";
  if (others.includes("macro") && !others.includes("controller")) return "controller";
  return id.includes("pxn") ? "controller" : "macro";
};

export const axisToDial = (value) => Math.max(1, Math.min(10, Math.round(((value + 1) / 2) * 9) + 1));

export const simulate = (detail) => window.dispatchEvent(new CustomEvent("spore-console", { detail }));
